import React, { useMemo } from "react";
import { GeoJSON } from "react-leaflet";
import type { Layer, PathOptions } from "leaflet";
import type { Geometry } from "geojson";
import type { AreaModel } from "../../../types/Area";
import {
  NeighborhoodFeatureCollection,
  NeighborhoodProperties,
} from "../../../types/CommunityImpact";

type NeighborhoodPolygonLayerProps = {
  neighborhoods: NeighborhoodFeatureCollection;
  areas?: AreaModel[];
  selectedAreaId?: string | null;
  onSelectArea?: (area: AreaModel | null) => void;
  disableGlow?: boolean;
};

const getEngagementStyle = (
  area: AreaModel | null | undefined,
): PathOptions => {
  const engagement = area?.engagement ?? 0;

  if (engagement >= 5) {
    return {
      color: "#16A34A",
      weight: 2,
      fillColor: "#22C55E",
      fillOpacity: 0.2,
    };
  }

  if (engagement >= 2) {
    return {
      color: "#D97706",
      weight: 2,
      fillColor: "#FACC15",
      fillOpacity: 0.24,
    };
  }

  return {
    color: "#DC2626",
    weight: 2,
    fillColor: "#EF4444",
    fillOpacity: 0.18,
  };
};

const getHighlightedStyle = (
  area: AreaModel | null | undefined,
): PathOptions => ({
  ...getEngagementStyle(area),
  color: "#0891B2",
  weight: 3,
  fillOpacity: Math.max(
    Number(getEngagementStyle(area).fillOpacity || 0.18),
    0.3,
  ),
});

const getCoordinatePairs = (value: unknown): number[][] => {
  if (!Array.isArray(value)) {
    return [];
  }

  if (
    value.length >= 2 &&
    typeof value[0] === "number" &&
    typeof value[1] === "number"
  ) {
    return [value as number[]];
  }

  return value.flatMap(getCoordinatePairs);
};

const normalizeName = (value?: string) =>
  (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const getGeometryCenter = (geometry: Geometry | null | undefined) => {
  const coordinates =
    geometry && "coordinates" in geometry ? geometry.coordinates : [];
  const pairs = getCoordinatePairs(coordinates);

  if (!pairs.length) {
    return { latitude: 35.1495, longitude: -90.049 };
  }

  const bounds = pairs.reduce(
    (result, pair) => ({
      minLng: Math.min(result.minLng, pair[0]),
      maxLng: Math.max(result.maxLng, pair[0]),
      minLat: Math.min(result.minLat, pair[1]),
      maxLat: Math.max(result.maxLat, pair[1]),
    }),
    {
      minLng: Number.POSITIVE_INFINITY,
      maxLng: Number.NEGATIVE_INFINITY,
      minLat: Number.POSITIVE_INFINITY,
      maxLat: Number.NEGATIVE_INFINITY,
    },
  );

  return {
    latitude: (bounds.minLat + bounds.maxLat) / 2,
    longitude: (bounds.minLng + bounds.maxLng) / 2,
  };
};

const getFallbackArea = (
  properties: NeighborhoodProperties,
  geometry: Geometry | null | undefined,
): AreaModel => {
  const center = getGeometryCenter(geometry);

  return {
    organization_population_id: properties.id,
    area_name: properties.name,
    latitude: center.latitude,
    longitude: center.longitude,
    zone_type: "community",
    adoption_rate: 0,
    engagement: 0,
    avg_session_time: 0,
    daily_active_users: 0,
    peak_usage_time: "-",
    strategic_insight: "",
    total_users: 0,
    population: 0,
  };
};

export default function NeighborhoodPolygonLayer({
  neighborhoods,
  areas = [],
  selectedAreaId,
  onSelectArea,
  disableGlow = false,
}: NeighborhoodPolygonLayerProps) {
  const areasById = useMemo(() => {
    return new Map(
      areas.map((area) => [area.organization_population_id, area] as const),
    );
  }, [areas]);

  const areasByName = useMemo(() => {
    return new Map(areas.map((area) => [normalizeName(area.area_name), area]));
  }, [areas]);

  const getAreaForFeature = (
    properties: NeighborhoodProperties,
    geometry: Geometry | null | undefined,
  ) =>
    areasById.get(properties.id) ||
    areasByName.get(normalizeName(properties.name)) ||
    getFallbackArea(properties, geometry);

  return (
    <GeoJSON
      data={neighborhoods}
      style={(feature) => {
        const properties = feature?.properties as
          | NeighborhoodProperties
          | undefined;

        if (!properties) {
          return getEngagementStyle(null);
        }

        if (properties.id === selectedAreaId) {
          return disableGlow
            ? getEngagementStyle(getAreaForFeature(properties, feature?.geometry))
            : getHighlightedStyle(
                getAreaForFeature(properties, feature?.geometry),
              );
        }

        return getEngagementStyle(
          getAreaForFeature(properties, feature?.geometry),
        );
      }}
      onEachFeature={(feature, layer: Layer) => {
        const properties = feature.properties as NeighborhoodProperties;
        const area = getAreaForFeature(properties, feature.geometry);
        const interactiveLayer = layer as Layer & {
          bindPopup?: (html: string) => void;
          on?: (eventMap: Record<string, () => void>) => void;
          setStyle?: (style: PathOptions) => void;
        };

        const engagementRate = area.engagement ?? 0;
        const popupContent = `
          <div class="text-sm">
            <div class="text-[#00BCD4] font-semibold">${properties.name}</div>
            <div class="text-[#4A5565]">${engagementRate.toFixed(2)}% Engagement</div>
          </div>
        `;

        if (interactiveLayer.bindPopup) {
          interactiveLayer.bindPopup(
            popupContent,
          );
        }

        if (interactiveLayer.on) {
          interactiveLayer.on({
            mouseover: () => {
              if (disableGlow) {
                return;
              }

              if (interactiveLayer.setStyle) {
                interactiveLayer.setStyle(getHighlightedStyle(area));
              }
            },
            mouseout: () => {
              if (interactiveLayer.setStyle) {
                interactiveLayer.setStyle(
                  properties.id === selectedAreaId
                    ? disableGlow
                      ? getEngagementStyle(area)
                      : getHighlightedStyle(area)
                    : getEngagementStyle(area),
                );
              }
            },
            click: () => onSelectArea?.(area),
          });
        }
      }}
    />
  );
}
